import React from 'react';
import { ShopifyProduct, ShopifyVariant } from '../returns/ProductResponse';
import InfoCard from './InfoCard';
import { getDiscountInfo } from '../../utils/discountUtils';

export const getDiscountedPrice = (item: Item | null, orderDiscount?: string, orderTotal?: number) => {
  if (!item) return 0;

  // Use discountAllocations if available (preferred method)
  if (item.discount_allocations && Array.isArray(item.discount_allocations)) {
    const discountInfo = getDiscountInfo(item as any);
    return (discountInfo as any).discountedPrice;
  }

  // Fallback to legacy calculation
  const price = parseFloat(item?.price || "0.00");
  const itemDiscount = parseFloat(item?.total_discount || "0.00");
  const totalOrderDiscount = parseFloat(orderDiscount || "0.00");

  // If there's an item-specific discount, use that
  if (itemDiscount > 0) {
    return price - itemDiscount;
  }

  // If there's an order-level discount, calculate item's proportional share
  if (totalOrderDiscount > 0 && orderTotal && orderTotal > 0) {
    // Calculate what percentage of the total order this item represents
    const itemProportion = price / orderTotal;
    // Apply that percentage of the total discount to this item
    const itemDiscountShare = totalOrderDiscount * itemProportion;
    return price - itemDiscountShare;
  }

  return price;
}

export interface DiscountAllocation {
  amount: string;
  discount_application_index: number;
  amount_set: {
    shop_money: { amount: string; currency_code: string };
    presentment_money: { amount: string; currency_code: string };
  };
}

export interface Item {
  id: number
  name: string
  quantity: number,
  returnQuantity?: number,
  image_src?: string
  color?: string
  price?: string
  option_map: {
    [key: string]: string | null;
  }
  total_discount?: string
  size?: string
  product_id?: number
  variant_id?: number
  discount_allocations?: DiscountAllocation[]
  claimQuantity?: number; // Added for claims
}

export interface ItemCardProps {
  item: Item,
  style?: React.CSSProperties,
  horizontal?: boolean,
  cardStyle?: boolean,
  selected?: boolean,
  small?: boolean,
  onClick?: () => void,
  orderDiscount?: string,
  orderTotal?: number,
  flowType?: 'RETURN' | 'CLAIM', // Add flowType to distinguish between returns and claims
}

export const ItemCard = ({
  item,
  style = {},
  horizontal = false,
  cardStyle = true,
  selected = false,
  small = false,
  onClick = () => { },
  orderDiscount,
  orderTotal,
  flowType = 'RETURN', // Default to RETURN for backward compatibility
}: ItemCardProps) => {
  const displayName = item.name || '';
  const imageUrl = item.image_src;
  const details = Object.entries(item.option_map || {}).map(([key, value]) => (
    `${key}: ${value}`
  ));

  const price = item.price;

  // Use discount allocations if available, otherwise fallback to legacy logic
  let hasDiscount = false;
  let discountedPrice = parseFloat(price || "0");

  if (item.discount_allocations !== undefined && Array.isArray(item.discount_allocations)) {
    // Use discount allocations (even if empty array means no discounts)
    const discountInfo = getDiscountInfo(item as any);
    hasDiscount = (discountInfo as any).shouldShowCrossedOut;
    discountedPrice = (discountInfo as any).discountedPrice;
  } else {
    // Only fallback to legacy logic if discount_allocations property doesn't exist
    discountedPrice = getDiscountedPrice(item, orderDiscount, orderTotal);
    hasDiscount = parseFloat(orderDiscount || "0.00") > 0 || parseFloat(item?.total_discount || "0.00") > 0;
  }

  return (
    <InfoCard
      headerText={displayName}
      style={{
        ...(horizontal ? {} : { minWidth: '18rem', maxWidth: '22rem' }),
        ...style
      }}
      details={details}
      imageUrl={imageUrl}
      horizontal={horizontal}
      cardStyle={cardStyle}
      small={small}
      selected={selected}
      endElements={
        <>
          <p><span style={{ textDecoration: hasDiscount ? 'line-through' : 'none' }}>${price}</span></p>
          {hasDiscount && (
            <p>${discountedPrice.toFixed(2)}</p>
          )}
          <p>Qty: {flowType === 'CLAIM' ? (item.claimQuantity || item.quantity) : (item.returnQuantity || item.quantity)}</p>
        </>
      }
      onClick={onClick}
    />
  );
};

interface VariantCardProps {
  variant?: ShopifyVariant | null,
  style?: React.CSSProperties,
  cardStyle?: boolean,
  horizontal?: boolean,
  shopifyProduct?: ShopifyProduct | null,
  selected?: boolean,
  onClick?: () => void,
}

export const VariantCard = ({
  variant = null,
  style = {},
  cardStyle = true,
  horizontal = false,
  shopifyProduct,
  selected = false,
  onClick = () => { },
}: VariantCardProps) => {

  const displayName = (shopifyProduct && shopifyProduct.title) || variant?.title || '';
  const optionMap = variant?.optionMap || {};
  const imageUrl = variant?.image_id ?? shopifyProduct?.image?.src;
  const details = Object.entries(optionMap).map(([key, value]) => (
    `${key}: ${value}`
  ));

  // TODO: add discount handling 
  const price = variant?.price;
  const quantity = variant?.quantity ?? 1;

  return (
    <InfoCard
      headerText={displayName}
      style={{
        ...(horizontal ? {} : { minWidth: '18rem', maxWidth: '22rem' }),
        ...style
      }}
      details={details}
      imageUrl={imageUrl}
      horizontal={horizontal}
      cardStyle={cardStyle}
      selected={selected}
      endElements={
        <>
          {variant && (
            <>
              <p>${price}</p>
              <p>Qty: {quantity}</p>
            </>
          )}
        </>
      }
      onClick={onClick}
    />
  );
};

interface ShopifyCardProps {
  className?: string,
  cardStyle?: boolean,
  onClick?: () => void,
  shopifyProduct: ShopifyProduct,
}

export const ShopifyCard = ({
  className = "",
  cardStyle = true,
  shopifyProduct,
  onClick = () => { },
}: ShopifyCardProps) => {

  const displayName = shopifyProduct.title;
  const defaultVariant = shopifyProduct.variants?.[0] ?? null;
  const price = defaultVariant?.price;
  const imageUrl = defaultVariant?.image_id || shopifyProduct?.image?.src;

  return (
    <div className={`shopify-card ${className}`}>
      <InfoCard
        headerText={displayName}
        cardStyle={cardStyle}
        imageUrl={imageUrl}
        endElements={
          <>
            {price && (<p>${price}</p>)}
          </>
        }
        onClick={onClick}
      />
    </div>
  );

};